import React, { useEffect } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { CheckIcon, WarningIcon } from '@modernloop/shared/icons';
import { Alert, AlertTitle, Avatar, Stack, Table, TableCell, TableRow, Typography } from '@mui/material';

import { LoggerEvent, useLogEvent } from 'src/hooks/useLogEvent';

type Props = {
  fullName: string;
  imageUrl: string;
  email: string;
  candidateCalendarLabel: string;
  candidateId: string;
  candidateEventCalendarId: string;
  interviewerEventCalendarId: string;
  candidateCalendarFound: boolean;
  interviewerCalendarLabel: string;
  interviewerCalendarFound: boolean;
  onClose: () => void;
};

const CalendarPermissionDialog = ({
  fullName,
  imageUrl,
  email,
  candidateCalendarLabel,
  candidateId,
  candidateEventCalendarId,
  interviewerEventCalendarId,
  candidateCalendarFound,
  interviewerCalendarLabel,
  interviewerCalendarFound,
  onClose,
}: Props) => {
  const { t } = useTranslation('hiring-dashboard-schedule');
  const { logEvent } = useLogEvent();
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    logEvent(LoggerEvent.CLIENT_NO_CALENDAR_ACCESS_DIALOG_SHOWN, {
      candidateId,
      candidateEventAccess: candidateCalendarFound,
      interviewerEventAccess: interviewerCalendarFound,
      candidateEventCalendarId,
      interviewerEventCalendarId,
    });
  }, [
    candidateCalendarFound,
    candidateEventCalendarId,
    candidateId,
    interviewerCalendarFound,
    interviewerEventCalendarId,
    logEvent,
  ]);

  return (
    <Dialog
      open
      dataTestId="schedule-object-calendar-permission-dialog"
      title={t('schedule.content.calendarAccessDenied')}
      onClose={onClose}
      submitOptions={{
        dataTestId: 'schedule-object-calendar-permission-dialog-okay-button',
        label: t('schedule.content.okay'),
        onClick: onClose,
      }}
    >
      <Stack>
        <Typography>{t('schedule.content.calendarAccessDeniedBody')}</Typography>

        <Table>
          <TableRow>
            <TableCell sx={{ borderBottom: 'none', p: (theme) => theme.spacing(1, 0) }}>
              <Typography variant="subtitle2">{t('schedule.content.scheduler')}</Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none', p: (theme) => theme.spacing(1, 0) }}>
              <Alert icon={<Avatar src={imageUrl} />} sx={{ backgroundColor: 'transparent', p: 0 }}>
                <AlertTitle>{fullName}</AlertTitle>
                {email}
              </Alert>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ borderBottom: 'none', p: (theme) => theme.spacing(1, 0) }}>
              <Typography variant="subtitle2">{t('schedule.content.candidateEvents')}</Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none', p: (theme) => theme.spacing(1, 0) }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {candidateCalendarFound ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                <Typography variant="subtitle2">{candidateCalendarLabel}</Typography>
              </Stack>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ borderBottom: 'none', p: (theme) => theme.spacing(1, 0) }}>
              <Typography variant="subtitle2">{t('schedule.content.interviewerEvents')}</Typography>
            </TableCell>
            <TableCell sx={{ borderBottom: 'none', p: (theme) => theme.spacing(1, 0) }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {interviewerCalendarFound ? <CheckIcon color="success" /> : <WarningIcon color="warning" />}
                <Typography variant="subtitle2">{interviewerCalendarLabel}</Typography>
              </Stack>
            </TableCell>
          </TableRow>
        </Table>
      </Stack>
    </Dialog>
  );
};

export default CalendarPermissionDialog;
